/**
 * aeterna/neural/backbone.ts — Unified Representation Backbone
 *
 * Creates a shared 128-dimensional space that every module projects
 * into via fixed random projections (Johnson-Lindenstrauss).
 *
 * IMPORTANT LIMITATIONS:
 * Random JL projections preserve INTRA-module distances reliably
 * (two affect vectors that are close stay close after projection).
 * INTER-module comparisons (affect vs room) are geometrically
 * meaningful only as a rough locality signal, NOT as true semantic
 * alignment. For trained cross-modal coherence, use CrossModalBinder
 * which has 5-way contrastive-trained projection heads.
 *
 * What this module IS good for:
 *   - Building a single bundled vector that captures "this moment"
 *   - Storing multimodal snapshots in Hopfield (recall is approximate)
 *   - Detecting which module dominates a recalled pattern
 *   - Computing intra-module distance in a common dimensionality
 *
 * What this module is NOT:
 *   - A substitute for trained cross-modal alignment
 *   - Proof that affect and room are "coherent" (use CrossModalBinder)
 */

import {
  zeros, normalize, cosine, vadd, vscale, matvec,
  randomMatrix, f32ToBase64, base64ToF32,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Shared backbone dimensionality */
export const BACKBONE_DIM = 128;

/** Input dimensionalities for each module */
const MODULE_DIMS = {
  affect:      8,   // CharAffect: warmth, tension, curiosity, melancholy, playfulness, depth, urgency, openness
  personality: 4,   // Liquid: patience, curiosity, warmth, wit
  semantic:    64,  // Embedding: TF-IDF → JL projection
  room:        32,  // CrossModal: ROOM_DIM features
  drive:       5,   // HomeostaticDrives: stimulation, comprehension, connection, rest, novelty
  arc:         6,   // Narrative: phase(one-hot-ish), tension, tensionDeriv, depth, progress, confidence
} as const;

export type BackboneModule = keyof typeof MODULE_DIMS;

/** Weights for unified state composition */
const BUNDLE_WEIGHTS: Record<BackboneModule, number> = {
  semantic:    0.30,  // what they said dominates
  personality: 0.20,  // who the Aeterna is
  affect:      0.18,  // how they feel
  room:        0.12,  // where they are
  drive:       0.10,  // what the Aeterna wants
  arc:         0.10,  // narrative position
};

// ─── Backbone ────────────────────────────────────────────────────────────────

export class RepresentationBackbone {
  /** Projection matrices: BACKBONE_DIM × module_dim, one per module */
  private projections: Map<BackboneModule, Float32Array> = new Map();

  /** Reusable buffers to avoid per-call allocation */
  private projBuf: Float32Array = zeros(BACKBONE_DIM);
  private unifiedBuf: Float32Array = zeros(BACKBONE_DIM);

  constructor(seed = 7777) {
    // Initialize fixed random projections for each module
    // JL guarantee: distances preserved within (1±ε) for ε ≈ sqrt(8 ln(n) / d)
    // With d=128 and n≈1000 stored patterns, ε ≈ 0.66 — adequate for cosine similarity
    let seedOffset = 0;
    for (const [module, dim] of Object.entries(MODULE_DIMS)) {
      const sigma = 1 / Math.sqrt(dim);
      this.projections.set(
        module as BackboneModule,
        randomMatrix(BACKBONE_DIM, dim, sigma, seed + seedOffset),
      );
      seedOffset += 1000; // large offset to avoid seed correlation
    }
  }

  // ─── Projection ───────────────────────────────────────────────────────

  /** Project a module-space vector into the shared backbone space */
  project(module: BackboneModule, vec: Float32Array): Float32Array {
    const P = this.projections.get(module)!;
    const dim = MODULE_DIMS[module];
    const projected = matvec(P, BACKBONE_DIM, dim, vec);
    return normalize(projected);
  }

  /** Project into backbone, reusing an output buffer */
  projectInto(module: BackboneModule, vec: Float32Array, out: Float32Array): Float32Array {
    const P = this.projections.get(module)!;
    const dim = MODULE_DIMS[module];
    matvec(P, BACKBONE_DIM, dim, vec, out);
    return normalize(out);
  }

  // ─── Coherence ────────────────────────────────────────────────────────

  /**
   * Cosine similarity between two projected vectors in backbone space.
   *
   * When both vectors come from the SAME module, this is a meaningful
   * distance metric (JL preserves intra-space distances).
   *
   * When vectors come from DIFFERENT modules, this is only a rough
   * locality heuristic. For true cross-modal alignment, use
   * CrossModalBinder.crossCoherence() which has trained projections.
   */
  coherence(
    moduleA: BackboneModule, vecA: Float32Array,
    moduleB: BackboneModule, vecB: Float32Array,
  ): number {
    const projA = this.project(moduleA, vecA);
    const projB = this.project(moduleB, vecB);
    return cosine(projA, projB);
  }

  // ─── Unified State ────────────────────────────────────────────────────

  /**
   * Compose the Aeterna's unified representation from all active module states.
   *
   * This is the vector that Hopfield should store — not raw affect or embedding.
   * Recall becomes naturally multimodal: a conversation about loneliness in a
   * dark room with depleted connection drive is a single point in backbone space.
   *
   * @param states Partial map of module → current state vector. Missing modules
   *               contribute zero (their absence is itself informative).
   * @returns Normalized 128-dim unified backbone vector.
   */
  unifiedState(states: Partial<Record<BackboneModule, Float32Array>>): Float32Array {
    const result = zeros(BACKBONE_DIM);

    for (const [module, weight] of Object.entries(BUNDLE_WEIGHTS)) {
      const vec = states[module as BackboneModule];
      if (!vec) continue;
      const projected = this.project(module as BackboneModule, vec);
      // Weighted accumulation
      for (let i = 0; i < BACKBONE_DIM; i++) {
        result[i] += projected[i] * weight;
      }
    }

    return normalize(result);
  }

  /**
   * Compute the unified state efficiently using pre-projected vectors.
   * Use this when you've already projected individual modules earlier in the tick.
   */
  unifiedFromProjected(projected: Partial<Record<BackboneModule, Float32Array>>): Float32Array {
    const result = zeros(BACKBONE_DIM);

    for (const [module, weight] of Object.entries(BUNDLE_WEIGHTS)) {
      const proj = projected[module as BackboneModule];
      if (!proj) continue;
      for (let i = 0; i < BACKBONE_DIM; i++) {
        result[i] += proj[i] * weight;
      }
    }

    return normalize(result);
  }

  // ─── Decomposition ────────────────────────────────────────────────────

  /**
   * Given a unified backbone vector (e.g., from Hopfield recall), estimate
   * which module contributed most to the pattern.
   *
   * Returns the module whose projection has the highest alignment with the
   * backbone vector. This tells you: "this memory is primarily an *affect*
   * memory" vs "this memory is primarily a *semantic* memory."
   */
  dominantModule(backboneVec: Float32Array, states: Partial<Record<BackboneModule, Float32Array>>): {
    module: BackboneModule;
    alignment: number;
  } {
    let bestModule: BackboneModule = "semantic";
    let bestAlign = -Infinity;

    for (const [module, vec] of Object.entries(states)) {
      if (!vec) continue;
      const projected = this.project(module as BackboneModule, vec);
      const alignment = cosine(projected, backboneVec);
      if (alignment > bestAlign) {
        bestAlign = alignment;
        bestModule = module as BackboneModule;
      }
    }

    return { module: bestModule, alignment: bestAlign };
  }

  // ─── Narrative Arc Encoding ───────────────────────────────────────────

  /**
   * Encode a NarrativeState into a 6-dim float vector suitable for backbone
   * projection. This bridges the struct-based narrative output to the
   * geometric backbone space.
   */
  static encodeNarrativeArc(
    phase: string,
    tension: number,
    tensionDeriv: number,
    depth: number,
    progress: number,
    confidence: number,
  ): Float32Array {
    const v = zeros(6);
    // Phase as a soft position on [0,1] — exposition=0, climax=0.5, resolution=1
    const phaseMap: Record<string, number> = {
      idle: 0, exposition: 0.15, rising: 0.35, climax: 0.55, falling: 0.75, resolution: 0.95,
    };
    v[0] = phaseMap[phase] ?? 0;
    v[1] = tension;
    v[2] = tensionDeriv * 0.5 + 0.5;  // center derivative around 0.5
    v[3] = depth;
    v[4] = progress;
    v[5] = confidence;
    return v;
  }

  // ─── Serialization ────────────────────────────────────────────────────

  /** Projections are deterministic from seed — no need to serialize them. */
  serialize(): null { return null; }
  static deserialize(_data: unknown, seed = 7777): RepresentationBackbone {
    return new RepresentationBackbone(seed);
  }
}
